import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { useParams } from 'react-router-dom';
import QRCode from 'qrcode';
import {
  Link as LinkIcon, Globe, Mail, Phone, Calendar, ShoppingCart, Music, FileText, MapPin,
  Sun, Moon, Share2, QrCode, X, Check,
} from 'lucide-react';
import { useLP } from '../linkpages/texts.js';
import { BRAND_ICONS } from '../linkpages/brandIcons.js';
import { buildLinkButton, buildPageStyles, rgba, type LinkItem, type LinkPage } from '../linkpages/themes.js';

// Generic lucide icons for non-brand link types
const ICON_COMPONENTS: Record<string, typeof LinkIcon> = {
  link: LinkIcon, globe: Globe, mail: Mail, phone: Phone,
  calendar: Calendar, shopping: ShoppingCart, music: Music, file: FileText, map: MapPin,
};

/** Brand logos (real SVG paths) where available, generic icons otherwise. */
function LinkGlyph({ icon, size }: { icon: string; size: number }) {
  const brand = BRAND_ICONS[icon];
  if (brand) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ flexShrink: 0 }}>
        <path d={brand} />
      </svg>
    );
  }
  const Generic = ICON_COMPONENTS[icon] ?? LinkIcon;
  return <Generic size={size} aria-hidden="true" style={{ flexShrink: 0 }} />;
}

const glassButton: CSSProperties = {
  background: 'rgba(255,255,255,0.28)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  border: '1px solid rgba(255,255,255,0.25)',
  borderRadius: '50%',
  padding: 10,
  color: '#ffffff',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
};

interface ViewProps {
  page: Pick<LinkPage, 'title' | 'slug' | 'bio' | 'avatar' | 'banner' | 'design' | 'links'>;
  /** preview disables navigation and click tracking (used in the editor) */
  preview?: boolean;
  onLinkClick?: (link: LinkItem) => void;
  /** visitor actions in the banner (mode toggle, share, QR). Public page only. */
  actions?: boolean;
  /** visitor-chosen light/dark override */
  modeOverride?: string;
  onToggleMode?: () => void;
  /** absolute public URL, used for share + QR */
  pageUrl?: string;
}

export function LinkPageView({ page, preview = false, onLinkClick, actions = false, modeOverride, onToggleMode, pageUrl }: ViewProps) {
  const lp = useLP();
  const design = modeOverride ? { ...page.design, mode: modeOverride } : page.design;
  const styles = buildPageStyles(design);
  const isGrid = styles.resolved.layout === 'grid';
  const isCard = styles.resolved.layout === 'card';
  const initial = (page.title || page.slug || '?').trim().charAt(0).toUpperCase();
  const links = (page.links || []).filter((l) => l.enabled !== false);

  const [showQr, setShowQr] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!showQr || !pageUrl || qrDataUrl) return;
    QRCode.toDataURL(pageUrl, { margin: 1, width: 320, errorCorrectionLevel: 'M' })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(''));
  }, [showQr, pageUrl, qrDataUrl]);

  const share = async () => {
    if (!pageUrl) return;
    try {
      if (navigator.share) {
        await navigator.share({ title: page.title || page.slug, url: pageUrl });
        return;
      }
      throw new Error('no web share');
    } catch {
      try {
        await navigator.clipboard.writeText(pageUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      } catch { /* clipboard unavailable */ }
    }
  };

  const trackAndFollow = (e: React.MouseEvent<HTMLAnchorElement>, link: LinkItem) => {
    if (preview) { e.preventDefault(); return; }
    onLinkClick?.(link);
  };

  const renderLink = (link: LinkItem) => {
    // per-link colour override: rebuild the button with the link's own accent
    const baseButton = link.color
      ? buildLinkButton(styles.resolved, link.color)
      : (styles.button as Record<string, string | number>);
    const external = /^https?:/i.test(link.url);
    const style: CSSProperties = {
      ...(baseButton as CSSProperties),
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '14px 18px',
      textDecoration: 'none',
      fontWeight: 600,
      fontSize: 16,
      transition: 'transform .15s ease',
      cursor: preview ? 'default' : 'pointer',
      ...(isGrid ? { flexDirection: 'column', justifyContent: 'center', textAlign: 'center', minHeight: 96 } : {}),
    };
    return (
      <a
        key={link.id}
        data-testid="lp-link"
        href={preview ? undefined : link.url}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
        onClick={(e) => trackAndFollow(e, link)}
        style={style}
        onMouseEnter={(e) => { if (!preview) e.currentTarget.style.transform = 'translateY(-2px)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
      >
        <LinkGlyph icon={link.icon} size={isGrid ? 26 : 20} />
        <span style={{ flex: isGrid ? 'none' : 1, textAlign: isGrid ? 'center' : 'left', wordBreak: 'break-word' }}>{link.label}</span>
      </a>
    );
  };

  /** Round social-style icon with the label underneath (ported from the VCARD card design). */
  const renderCircle = (link: LinkItem) => {
    const c = link.color || styles.resolved.accent;
    const external = /^https?:/i.test(link.url);
    return (
      <a
        key={link.id}
        data-testid="lp-circle"
        href={preview ? undefined : link.url}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
        onClick={(e) => trackAndFollow(e, link)}
        style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
          width: 88, textDecoration: 'none', cursor: preview ? 'default' : 'pointer',
        }}
      >
        <span
          style={{
            width: 62, height: 62, borderRadius: '50%', background: rgba(c, 0.24), color: c,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `1px solid ${rgba(c, 0.3)}`, transition: 'transform .15s ease',
          }}
          onMouseEnter={(e) => { if (!preview) e.currentTarget.style.transform = 'scale(1.08)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
        >
          <LinkGlyph icon={link.icon} size={26} />
        </span>
        <span
          style={{
            fontSize: 10, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase',
            color: styles.muted, textAlign: 'center', maxWidth: 88,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
        >
          {link.label}
        </span>
      </a>
    );
  };

  // group links into sections separated by headers
  const blocks: { header: LinkItem | null; items: LinkItem[] }[] = [];
  let current: { header: LinkItem | null; items: LinkItem[] } | null = null;
  links.forEach((link) => {
    if (link.type === 'header') {
      current = { header: link, items: [] };
      blocks.push(current);
    } else {
      if (!current) { current = { header: null, items: [] }; blocks.push(current); }
      current.items.push(link);
    }
  });

  /** Consecutive 'circle' links render together in one flex row. */
  const renderItems = (items: LinkItem[]) => {
    if (isGrid) {
      return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
          {items.map(renderLink)}
        </div>
      );
    }
    const out: React.ReactNode[] = [];
    let circleRun: LinkItem[] = [];
    const flushCircles = (key: string) => {
      if (!circleRun.length) return;
      out.push(
        <div key={key} style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
          {circleRun.map(renderCircle)}
        </div>,
      );
      circleRun = [];
    };
    items.forEach((item, i) => {
      if (item.display === 'circle') {
        circleRun.push(item);
      } else {
        flushCircles(`c${i}`);
        out.push(renderLink(item));
      }
    });
    flushCircles('cEnd');
    return <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{out}</div>;
  };

  const actionButtons = actions && (
    <div style={{ position: 'absolute', top: 14, right: 14, display: 'flex', gap: 8, zIndex: 2 }}>
      {onToggleMode && (
        <button type="button" style={glassButton} onClick={onToggleMode} aria-label={lp('toggleMode')} title={lp('toggleMode')}>
          {styles.resolved.mode === 'light' ? <Sun size={18} /> : <Moon size={18} />}
        </button>
      )}
      <button type="button" style={glassButton} onClick={() => void share()} aria-label={lp('share')} title={lp('share')}>
        {copied ? <Check size={18} /> : <Share2 size={18} />}
      </button>
      <button type="button" style={glassButton} onClick={() => setShowQr(true)} aria-label={lp('showQr')} title={lp('showQr')}>
        <QrCode size={18} />
      </button>
    </div>
  );

  const qrOverlay = showQr && (
    <div
      role="dialog" aria-modal="true"
      style={{
        position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)', padding: 20,
      }}
      onClick={() => setShowQr(false)}
    >
      <div
        style={{ background: '#ffffff', color: '#111111', borderRadius: 20, padding: '24px 24px 16px', maxWidth: 360, width: '100%', textAlign: 'center' }}
        onClick={(e) => e.stopPropagation()}
      >
        {qrDataUrl
          ? <img src={qrDataUrl} alt="QR" style={{ width: '100%', height: 'auto', borderRadius: 8 }} />
          : <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.5 }}>…</div>}
        <p style={{ fontWeight: 700, margin: '12px 0 2px' }}>{page.title}</p>
        <p style={{ margin: '0 0 12px', fontSize: 12, opacity: 0.6, wordBreak: 'break-all' }}>{pageUrl}</p>
        <button
          type="button"
          onClick={() => setShowQr(false)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none', background: '#111111', color: '#ffffff', borderRadius: 999, padding: '10px 22px', fontWeight: 600, cursor: 'pointer' }}
        >
          <X size={16} /> {lp('close')}
        </button>
      </div>
    </div>
  );

  const headerText = isCard ? styles.card.surfaceText : (styles.page['color'] as string);

  const avatarBlock = (
    <>
      {page.avatar ? (
        <img src={page.avatar} alt="" style={{ width: 96, height: 96, borderRadius: '50%', objectFit: 'cover', border: `3px solid ${styles.avatarRing}`, background: styles.card.surface }} />
      ) : (
        <div
          aria-hidden="true"
          style={{
            width: 96, height: 96, borderRadius: '50%', margin: '0 auto', display: 'flex',
            alignItems: 'center', justifyContent: 'center', fontSize: 38, fontWeight: 700,
            background: styles.button['background'] as string, color: styles.button['color'] as string,
            border: `3px solid ${styles.avatarRing}`,
          }}
        >
          {initial}
        </div>
      )}
    </>
  );

  // Visitenkarl brand footer (logo variants for light/dark surfaces)
  const surfaceIsDark = styles.card.surfaceText === '#fafafa';
  const brandFooter = (
    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 30, opacity: 0.85 }}>
      <img
        src={surfaceIsDark ? '/graphics/visitenkarl-logo-dark.svg' : '/graphics/visitenkarl-logo.svg'}
        alt="Visitenkarl"
        style={{ height: 26, width: 'auto' }}
      />
    </div>
  );

  const sectionsBlock = (
    <>
      {blocks.length === 0 && (
        <p style={{ textAlign: 'center', color: styles.muted }}>{preview ? lp('previewEmpty') : ''}</p>
      )}
      {blocks.map((block, i) => (
        <section key={block.header ? block.header.id : `b${i}`} style={{ marginBottom: 22 }}>
          {block.header && (
            <h2
              style={{
                fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700,
                color: styles.muted, margin: '0 0 10px', paddingBottom: 6, borderBottom: `1px solid ${styles.headerLine}`,
              }}
            >
              {block.header.label}
            </h2>
          )}
          {renderItems(block.items)}
        </section>
      ))}
    </>
  );

  // ── Card layout: banner (image or gradient) + overlapping avatar on a card ─
  if (isCard) {
    return (
      <div
        style={{ ...(styles.page as CSSProperties), display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '40px 20px 40px', boxSizing: 'border-box', flex: 1 }}
        data-testid="lp-page"
      >
        <div
          style={{
            width: '100%', maxWidth: 520, borderRadius: 28, overflow: 'hidden',
            background: styles.card.surface, color: styles.card.surfaceText,
            boxShadow: '0 18px 50px rgba(0,0,0,0.28)',
          }}
        >
          <div style={{ position: 'relative', height: 176, background: styles.card.banner }}>
            {page.banner && (
              <img src={page.banner} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            )}
            {actionButtons}
          </div>
          <div style={{ padding: '0 24px 28px' }}>
            <div style={{ marginTop: -52, marginBottom: 14 }}>
              <span style={{ display: 'inline-block', borderRadius: '50%', padding: 4, background: styles.card.surface }}>
                {avatarBlock}
              </span>
            </div>
            <div style={{ marginBottom: 24 }}>
              <h1 style={{ fontSize: 27, fontWeight: 800, letterSpacing: '-0.01em', margin: '0 0 6px', color: headerText }} data-testid="lp-title">{page.title}</h1>
              {page.bio && <p style={{ margin: 0, color: styles.card.surfaceMuted, fontSize: 15, lineHeight: 1.5, whiteSpace: 'pre-line' }}>{page.bio}</p>}
            </div>
            {sectionsBlock}
            {brandFooter}
          </div>
        </div>
        {qrOverlay}
      </div>
    );
  }

  return (
    <div
      style={{ ...(styles.page as CSSProperties), display: 'flex', justifyContent: 'center', padding: '48px 20px 32px', boxSizing: 'border-box', flex: 1 }}
      data-testid="lp-page"
    >
      <div style={{ width: '100%', maxWidth: 520, position: 'relative' }}>
        {actionButtons}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          {avatarBlock}
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: '16px 0 6px' }} data-testid="lp-title">{page.title}</h1>
          {page.bio && <p style={{ margin: 0, color: styles.muted, fontSize: 15, lineHeight: 1.5, whiteSpace: 'pre-line' }}>{page.bio}</p>}
        </div>
        {sectionsBlock}
        {brandFooter}
      </div>
      {qrOverlay}
    </div>
  );
}

export default function LinkPagePublic() {
  const lp = useLP();
  const { slug = '' } = useParams();
  const [state, setState] = useState<{ status: 'loading' | 'ok' | 'notfound'; page: LinkPage | null }>({ status: 'loading', page: null });
  // visitor light/dark override: null = follow the page design
  const [modeOverride, setModeOverride] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', page: null });
    setModeOverride(null);
    fetch(`/api/public/l/${encodeURIComponent(slug)}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status));
        return (await res.json()) as LinkPage;
      })
      .then((page) => {
        if (cancelled) return;
        document.title = page.title || slug;
        setState({ status: 'ok', page });
      })
      .catch(() => { if (!cancelled) setState({ status: 'notfound', page: null }); });
    return () => { cancelled = true; };
  }, [slug]);

  const trackClick = (link: LinkItem) => {
    try {
      // sendBeacon survives page navigation; fall back to a keepalive fetch
      const url = `/api/public/l/${encodeURIComponent(slug)}/click/${encodeURIComponent(link.id)}`;
      if (!(navigator.sendBeacon && navigator.sendBeacon(url))) {
        void fetch(url, { method: 'POST', keepalive: true });
      }
    } catch { /* tracking must never break navigation */ }
  };

  if (state.status === 'loading') {
    return <div style={{ minHeight: '100vh', background: '#0f172a' }} aria-busy="true" />;
  }
  if (state.status === 'notfound' || !state.page) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', color: '#fff', fontFamily: 'system-ui, sans-serif' }}>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: 24, margin: '0 0 8px' }}>{lp('notFoundTitle')}</h1>
          <p style={{ margin: 0, opacity: 0.7 }}>{lp('notFoundText')}</p>
        </div>
      </div>
    );
  }

  // effective mode after the visitor toggle: the design default decides the start,
  // the toggle flips between light and dark from there
  const baseMode = state.page.design.mode === 'auto'
    ? (['paper'].includes(state.page.design.theme) ? 'light' : 'dark')
    : state.page.design.mode;
  const effectiveMode = modeOverride ?? baseMode;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <LinkPageView
        page={state.page}
        onLinkClick={trackClick}
        actions
        pageUrl={window.location.href}
        modeOverride={effectiveMode}
        onToggleMode={() => setModeOverride(effectiveMode === 'dark' ? 'light' : 'dark')}
      />
    </div>
  );
}
